/*
 * =============================================================================
 * FILE HEADER — packages/healing/src/scores.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                |
 * |---|------------------------|-------------------|------------------|-----------------------------|
 * | 1 | 2026-06-17 (Phase 2)   | Claude Sonnet 4.6 | Healing core     | Created — 4 scoring fns     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/healing/src/heal.ts   (calls all score functions per candidate node)
 * - packages/healing/src/index.test.ts (unit tests for each function in isolation)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * ScoreComponents  — breakdown struct { semantic, geom, attr, neighbor }
 * WEIGHTS          — frozen weight tuple (0.45/0.25/0.20/0.10)
 * cosineScore(a, b) → number    — cosine similarity of two Float32Arrays
 * geomScore(actual, recorded, w, h) → number   — IoU of normalized rects
 * attrScore(node, fp) → number  — attribute token overlap ratio
 * neighborScore(node, fp, all) → number   — nearest-neighbor text overlap
 * compositeScore(components) → number   — weighted sum of all four
 *
 * CALLER → CALLEE MAP
 * --------------------
 * heal.ts → cosineScore, geomScore, attrScore, neighborScore, compositeScore
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. All functions are pure (no side effects, no I/O). Safe to run in tests
 *    without any model loaded.
 * 2. Weight constants are frozen from CLAUDE.md §4 and must not be changed
 *    without recalibrating acceptance thresholds.
 * 3. cosineScore clamps output to [-1, 1] to guard against float drift.
 * 4. geomScore uses IoU (Intersection over Union). A perfect bounds match
 *    returns 1.0; no overlap at all returns 0.0.
 * 5. attrScore does substring matching (not exact) so "Login" matches
 *    "com.example:id/btn_login". Direction is fp-token ⊆ nodeAttr OR
 *    nodeAttr ⊆ fp-token to handle both id-prefix and label-suffix cases.
 * 6. neighborScore returns 1.0 when no neighbors are recorded — the absence
 *    of neighbor data is not a signal of mismatch, just no context.
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
// ── Weights (frozen — recalibrate before changing) ────────────────────────────
export const WEIGHTS = Object.freeze({
    semantic: 0.45,
    geom: 0.25,
    attr: 0.20,
    neighbor: 0.10,
});
// ── cosineScore ───────────────────────────────────────────────────────────────
/**
 * Cosine similarity between two embedding vectors.
 * Returns 0 if either vector has zero magnitude.
 */
export function cosineScore(a, b) {
    if (a.length === 0 || a.length !== b.length)
        return 0;
    let dot = 0;
    let normA = 0;
    let normB = 0;
    for (let i = 0; i < a.length; i++) {
        const ai = a[i] ?? 0;
        const bi = b[i] ?? 0;
        dot += ai * bi;
        normA += ai * ai;
        normB += bi * bi;
    }
    if (normA === 0 || normB === 0)
        return 0;
    return Math.max(-1, Math.min(1, dot / (Math.sqrt(normA) * Math.sqrt(normB))));
}
// ── geomScore ─────────────────────────────────────────────────────────────────
/**
 * IoU of the actual node's bounds (normalized to screen size) vs the
 * fingerprint's recorded boundsRatio [x, y, w, h] (already 0..1).
 */
export function geomScore(actual, recorded, screenWidth, screenHeight) {
    if (screenWidth <= 0 || screenHeight <= 0)
        return 0;
    // Normalize actual bounds to [0,1]
    const ax = actual.left / screenWidth;
    const ay = actual.top / screenHeight;
    const aw = (actual.right - actual.left) / screenWidth;
    const ah = (actual.bottom - actual.top) / screenHeight;
    const [bx, by, bw, bh] = recorded;
    // Intersection rectangle
    const ix = Math.max(0, Math.min(ax + aw, bx + bw) - Math.max(ax, bx));
    const iy = Math.max(0, Math.min(ay + ah, by + bh) - Math.max(ay, by));
    const intersection = ix * iy;
    const union = aw * ah + bw * bh - intersection;
    return union <= 0 ? 0 : intersection / union;
}
// ── attrScore ─────────────────────────────────────────────────────────────────
/**
 * Attribute exact-match score.
 *
 * The fingerprint's semanticText is pipe-separated: "resourceId|text|contentDesc|className".
 * Each non-empty token is compared (substring) against all string attributes of
 * the candidate node. Score = matched_tokens / total_fp_tokens.
 */
export function attrScore(node, fingerprint) {
    const fpTokens = fingerprint.semanticText
        .split("|")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0);
    if (fpTokens.length === 0)
        return 0;
    const nodeAttrs = [
        node.resourceId,
        node.identifier,
        node.text,
        node.contentDesc,
        node.className,
        node.role,
    ]
        .filter(Boolean)
        .map((s) => s.toLowerCase());
    const matches = fpTokens.filter((token) => nodeAttrs.some((attr) => attr.includes(token) || token.includes(attr)));
    return matches.length / fpTokens.length;
}
// ── neighborScore ─────────────────────────────────────────────────────────────
/**
 * Neighbor text overlap score.
 *
 * Finds the nearest labeled node in each of the four compass directions from
 * the candidate node. Compares those actual neighbors against the neighbors
 * stored in the fingerprint. Score = matched_recorded / total_recorded.
 *
 * Returns 1.0 when no neighbors were recorded (no information = no penalty).
 */
export function neighborScore(node, fingerprint, allNodes) {
    const { above, below, left, right } = fingerprint.neighbors;
    const recorded = [above, below, left, right].filter(Boolean);
    if (recorded.length === 0)
        return 1; // no neighbor context stored
    const cx = (node.bounds.left + node.bounds.right) / 2;
    const cy = (node.bounds.top + node.bounds.bottom) / 2;
    let bestAbove = "";
    let bestBelow = "";
    let bestLeft = "";
    let bestRight = "";
    let minAbove = Infinity;
    let minBelow = Infinity;
    let minLeft = Infinity;
    let minRight = Infinity;
    for (const n of allNodes) {
        if (n === node)
            continue;
        const label = (n.text || n.contentDesc || n.resourceId || "").trim();
        if (!label)
            continue;
        const nx = (n.bounds.left + n.bounds.right) / 2;
        const ny = (n.bounds.top + n.bounds.bottom) / 2;
        const dy = ny - cy;
        const dx = nx - cx;
        if (dy < 0 && -dy < minAbove) {
            minAbove = -dy;
            bestAbove = label;
        }
        if (dy > 0 && dy < minBelow) {
            minBelow = dy;
            bestBelow = label;
        }
        if (dx < 0 && -dx < minLeft) {
            minLeft = -dx;
            bestLeft = label;
        }
        if (dx > 0 && dx < minRight) {
            minRight = dx;
            bestRight = label;
        }
    }
    const actual = [bestAbove, bestBelow, bestLeft, bestRight].filter(Boolean);
    const matches = recorded.filter((r) => {
        const rLow = r.toLowerCase();
        return actual.some((a) => {
            const aLow = a.toLowerCase();
            return aLow.includes(rLow) || rLow.includes(aLow);
        });
    });
    return matches.length / recorded.length;
}
// ── compositeScore ────────────────────────────────────────────────────────────
/** Weighted sum of the four component scores. Output is in [0, 1]. */
export function compositeScore(components) {
    return (WEIGHTS.semantic * components.semantic +
        WEIGHTS.geom * components.geom +
        WEIGHTS.attr * components.attr +
        WEIGHTS.neighbor * components.neighbor);
}
//# sourceMappingURL=scores.js.map